import { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAppAuth } from '../components/AuthContext.jsx';
import MaklerProfileEditor from '../components/MaklerProfileEditor.jsx';
import { usePolicy, levelFor } from '../components/PolicyContext.jsx';
import { getMaklerDetail, closeAndStartNextWeek, setMaklerPlan, addMaklerAbsence, removeMaklerAbsence, setMaklerSupport } from '../services/jrMaklerDetail.js';

const WEEKDAYS = ['Po', 'Út', 'St', 'Čt', 'Pá'];
const ACTIVITY_TYPES = {
  call: 'Oslovení', cold_call: 'Studené volání', door_knocking: 'Doorknocking',
  meeting: 'Schůzka', demo: 'Nabraná nemovitost / obchod',
};
const ACTIVITY_STATUSES = {
  scheduled: 'Naplánováno', completed: 'Uskutečněno',
  failed: 'Neúspěch', cancelled: 'Zrušeno',
};
const SOURCES = {
  sphere_10: 'Volání sféře vlivu', cold_call_54: 'Studené volání',
  door_knocking_11: 'Doorknocking', referral: 'Doporučení',
  social: 'Sociální sítě', online_leads: 'Online leady', flyers: 'Letáky do schránek',
  letters_area: 'Dopisy v lokalitě', letters_other: 'Dopisy k nemovitostem jiných makléřů',
  networking: 'Networking', rental_inquiries: 'Nájem – poptávky z inzerce', other: 'Ostatní',
};

function fmtDay(iso) {
  const d = new Date(iso);
  return Number.isNaN(d) ? '' : `${d.getDate()}. ${d.getMonth() + 1}.`;
}

function weekNo(iso) {
  const d = new Date(iso);
  const t = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  t.setUTCDate(t.getUTCDate() + 4 - (t.getUTCDay() || 7));
  const y0 = new Date(Date.UTC(t.getUTCFullYear(), 0, 1));
  return Math.ceil(((t - y0) / 86400000 + 1) / 7);
}

function semaforOf(pct, thresholds) {
  if (pct == null) return null;
  const n = Number(pct);
  if (n >= thresholds.greenFrom) return ['semafor--grn', 'Zelená'];
  if (n >= thresholds.orangeFrom) return ['semafor--org', 'Oranžová'];
  return ['semafor--red', 'Červená'];
}

export default function JRMaklerDetail() {
  const { policy } = usePolicy();
  const reasonCodes = policy.catalogues.reductionReasons.filter((r) => r.active);
  const minimumContacts = policy.calendarRules.minimumManualContacts;
  const { user } = useAppAuth();
  const isJch = user?.role === 'JCH';
  const { id } = useParams();
  const [makler, setMakler] = useState(null);
  const [plan, setPlan] = useState(null);
  const [history, setHistory] = useState([]);
  const [absences, setAbsences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState('');
  const [form, setForm] = useState({
    targetContacts: '',
    targetMeetings: '',
    targetAcquisitions: '',
    reason: '',
    reasonCode: '',
    jchConsent: false,
  });
  const [days, setDays] = useState([]);
  const [activities, setActivities] = useState([]);
  const [vac, setVac] = useState({ from: '', to: '' });
  const [support, setSupport] = useState({ on: false, note: '' });

  const load = async () => {
    setLoading(true);
    setError('');
    setStatus('');
    try {
      const data = await getMaklerDetail(id);
      setMakler(data.makler || null);
      setPlan(data.plan || null);
      setHistory(data.history || []);
      setAbsences(data.absences || []);
      setDays(data.days || []);
      setActivities(data.activities || []);
      setSupport({ on: !!data.makler?.support_mode, note: data.makler?.support_note || '' });
      if (data.plan) {
        setForm({
          targetContacts: data.plan.base_target_contacts ?? data.plan.target_contacts ?? '',
          targetMeetings: data.plan.base_target_meetings ?? data.plan.target_meetings ?? '',
          targetAcquisitions: data.plan.base_target_acquisitions ?? data.plan.target_acquisitions ?? '',
          reason: '',
          reasonCode: '',
          jchConsent: false,
        });
      } else {
        setForm({
          targetContacts: policy.weeklyDefaults.contacts,
          targetMeetings: policy.weeklyDefaults.meetings,
          targetAcquisitions: policy.weeklyDefaults.acquisitions,
          reason: '', reasonCode: '', jchConsent: false,
        });
      }
    } catch (err) {
      setError(err.message || 'Makléře se nepodařilo načíst.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [id]);

  const levelName = useMemo(() => {
    if (!makler) return '—';
    return levelFor(policy, makler)?.name ?? makler.level_name ?? '—';
  }, [makler, policy]);

  const updateForm = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleAdjust = async (event) => {
    event.preventDefault();
    if (event.nativeEvent.submitter?.value === 'next-week') {
      await handleCloseWeek();
      return;
    }
    setSaving(true);
    setError('');
    setStatus('');
    try {
      await setMaklerPlan(id, {
        targetContacts: form.targetContacts,
        targetMeetings: form.targetMeetings,
        targetAcquisitions: form.targetAcquisitions,
        reasonCode: form.reasonCode,
        reason: form.reason || null,
        jchConsent: form.jchConsent,
      });
      setStatus('Plán uložen.');
      await load();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Plán se nepodařilo uložit.');
    } finally {
      setSaving(false);
    }
  };

  const handleVacation = async (event) => {
    event.preventDefault();
    if (!vac.from) return;
    setError('');
    try {
      await addMaklerAbsence(id, vac.from, vac.to || vac.from);
      setVac({ from: '', to: '' });
      setStatus('Nepřítomnost zapsána.');
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Nepřítomnost se nepodařilo zapsat.');
    }
  };

  const handleRemoveAbsence = async (date) => {
    await removeMaklerAbsence(id, date);
    await load();
  };

  const handleSupport = async (next) => {
    setSupport(next);
    try {
      await setMaklerSupport(id, { supportMode: next.on, note: next.note });
      setStatus('Režim podpory uložen.');
    } catch {
      setError('Režim podpory se nepodařilo uložit.');
    }
  };

  const handleCloseWeek = async () => {
    if (!plan) return;
    setSaving(true);
    setError('');
    setStatus('');
    try {
      const next = await closeAndStartNextWeek(plan.id, {
        targetContacts: form.targetContacts,
        targetMeetings: form.targetMeetings === '' ? 0 : form.targetMeetings,
        targetAcquisitions: form.targetAcquisitions === '' ? 0 : form.targetAcquisitions,
        reasonCode: form.reasonCode,
        reason: form.reason || null,
        jchConsent: form.jchConsent,
      });
      await load();
      setStatus(`Týden uzavřen. Nový plán: ${fmtDay(next.week_start_date)} – ${fmtDay(next.week_end_date)}.`);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Týden se nepodařilo uzavřít.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="dashboard__status">Načítá se…</div>;
  }

  if (error && !makler) {
    return (
      <section className="dashboard">
        <p className="dashboard__error" role="alert">{error}</p>
        <Link className="button button--secondary" to="/dashboard/database">Zpět do databáze</Link>
      </section>
    );
  }

  return (
    <section className="dashboard">
      <div className="dashboard__header">
        <div>
          <h1 className="dashboard__title">{makler?.name || 'Makléř'}</h1>
          <p className="dashboard__lead">
            {makler?.position || '—'} · <span style={{ color: makler && levelFor(policy, makler)?.color }}>{levelName}</span>
            {isJch && ' · Přehled si otevíráš ze společných čísel, souhrn za tým máš každý čtvrtek v reportu.'}
          </p>
        </div>
        <Link className="button button--secondary" to={isJch ? '/dashboard/cisla' : '/dashboard/database'}>
          {isJch ? 'Společná čísla' : 'Zpět'}
        </Link>
        <button className="button button--secondary" type="button" onClick={load} disabled={saving}>
          Obnovit záznamy
        </button>
      </div>

      {error && <p className="dashboard__error" role="alert">{error}</p>}
      {status && <p className="dashboard__status" role="status">{status}</p>}

      <article className="card">
        <h2 className="card__title">Plán vs. realita · {history.length} týdnů</h2>
        <p className="card__hint">
          Barva jen z povinných oslovení · schůzky a nábory barvu nemění.
        </p>
        {history.length === 0 ? (
          <p className="card__text">Zatím žádné týdny.</p>
        ) : (
          <div className="jr-table-wrap">
            <table className="jr-table">
              <thead>
                <tr>
                  <th>Týden</th><th>Oslovení · plán</th><th>Realita</th>
                  <th>Plnění</th><th>Semafor</th><th>Schůzky plán / skut.</th><th>Nábory</th>
                </tr>
              </thead>
              <tbody>
                {history.map((w) => {
                  const sem = w.week_closed ? semaforOf(w.plan_completion_pct, policy.trafficLight) : null;
                  return (
                    <tr key={w.id}>
                      <td>
                        T{weekNo(w.week_start_date)}
                        <div className="card__hint">
                          {fmtDay(w.week_start_date)}–{fmtDay(w.week_end_date)}
                        </div>
                      </td>
                      <td>
                        {Number(w.target_contacts)}
                        {w.plan_adjustment_reason && (
                          <div className="card__hint">{w.plan_adjustment_reason}</div>
                        )}
                      </td>
                      <td>{w.contacts_completed}</td>
                      <td>
                        {w.week_closed && w.plan_completion_pct != null
                          ? `${Math.round(Number(w.plan_completion_pct))} %`
                          : 'dosud'}
                      </td>
                      <td>
                        {sem ? <span className={`semafor ${sem[0]}`}>{sem[1]}</span> : 'probíhá'}
                      </td>
                      <td>{w.target_meetings} / {w.meetings_completed}</td>
                      <td>{w.acquisitions_completed || '–'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {absences.length > 0 && (
          <p className="card__hint">
            Omluvená nepřítomnost (60 dní): {absences.map((a) => fmtDay(a.date)).join(', ')}.
            Za každý den se plán zkrátí o 15 oslovení (v přípravě o 8).
          </p>
        )}
      </article>

      <div className="dashboard__cards">
        <article className="card">
          <span className="card__caption">E-mail</span>
          <span className="card__big">{makler?.email || '—'}</span>
          <span className="card__hint">Úroveň: {levelName}</span>
        </article>

        <article className="card">
          <span className="card__caption">Stav</span>
          <span className="card__big">{makler?.status || '—'}</span>
          <span className="card__hint">Makléřské ID: {makler?.id || '—'}</span>
        </article>

        <article className="card">
          <span className="card__caption">Aktuální plán</span>
          <span className="card__big">{plan ? `${plan.plan_completion_pct ?? 0}%` : '—'}</span>
          <span className="card__hint">
            {plan ? `${plan.contacts_completed ?? 0} / ${plan.target_contacts ?? 0} oslovení` : 'Žádný otevřený týden'}
          </span>
          {plan && <span className="card__hint">{fmtDay(plan.week_start_date)} – {fmtDay(plan.week_end_date)}</span>}
        </article>
      </div>

      <div className="card">
        <h2 className="card__title">Plán makléře</h2>
        {plan ? (
          <div className="overview" role="table" aria-label="Plán makléře">
            <div className="overview__row overview__row--head">
              <span className="overview__head">Typ</span>
              <span className="overview__head">Cíl</span>
              <span className="overview__head">Hotovo</span>
              <span className="overview__head">Plnění</span>
              <span className="overview__head">Stav</span>
              <span className="overview__head">Poznámka</span>
            </div>
            <div className="overview__row">
              <span className="overview__cell">Oslovení</span>
              <span className="overview__cell">{plan.target_contacts ?? '—'}</span>
              <span className="overview__cell">{plan.contacts_completed ?? '—'}</span>
              <span className="overview__cell">{plan.plan_completion_pct ?? '—'}%</span>
              <span className="overview__cell">{plan.week_closed ? 'Uzavřeno' : 'Aktivní'}</span>
              <span className="overview__cell">{plan.reason || '—'}</span>
            </div>
            <div className="overview__row">
              <span className="overview__cell">Schůzky</span>
              <span className="overview__cell">{plan.target_meetings ?? '—'}</span>
              <span className="overview__cell">{plan.meetings_completed ?? '—'}</span>
              <span className="overview__cell">—</span>
              <span className="overview__cell">{plan.week_closed ? 'Uzavřeno' : 'Aktivní'}</span>
              <span className="overview__cell">—</span>
            </div>
            <div className="overview__row">
              <span className="overview__cell">Nábory</span>
              <span className="overview__cell">{plan.target_acquisitions ?? '—'}</span>
              <span className="overview__cell">{plan.acquisitions_completed ?? '—'}</span>
              <span className="overview__cell">—</span>
              <span className="overview__cell">{plan.week_closed ? 'Uzavřeno' : 'Aktivní'}</span>
              <span className="overview__cell">—</span>
            </div>
          </div>
        ) : (
          <p className="dashboard__status">Bez otevřeného plánu.</p>
        )}
      </div>

      <article className="card">
        <h2 className="card__title">Dny tohoto týdne</h2>
        <div className="jr-table-wrap">
          <table className="jr-table">
            <thead>
              <tr><th>Den</th><th>Povinná oslovení</th><th>Další oslovení</th><th>Schůzky</th><th>Nábory</th><th>Stav</th></tr>
            </thead>
            <tbody>
              {WEEKDAYS.map((name, i) => {
                const d = new Date();
                d.setDate(d.getDate() - ((d.getDay() + 6) % 7) + i);
                const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
                const row = days.find((x) => x.date === iso);
                const away = absences.some((a) => a.date === iso);
                const calendar = plan?.calendar_days?.find((day) => day.date === iso);
                const absence = absences.find((a) => a.date === iso);
                return (
                  <tr key={iso}>
                    <td>{name} {fmtDay(iso)}</td>
                    <td>{row ? Number(row.contacts) : '–'}</td>
                    <td>{row ? Number(row.additional_contacts) : '–'}</td>
                    <td>{row ? Number(row.meetings) : '–'}</td>
                    <td>{row ? Number(row.acquisitions) : '–'}</td>
                    <td>{calendar?.name || (absence?.absence_type === 'vacation' ? 'Dovolená'
                      : away ? 'Omluveno' : row ? 'Zapsáno' : '–')}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </article>

      <article className="card">
        <h2 className="card__title">Odeslané aktivity · posledních 60 dní</h2>
        <p className="card__hint">
          Záznamy uložené přes API včetně mobilní aplikace. Datum je den vykonání,
          nikoli den odeslání. Starší týden se nezobrazuje v tabulce tohoto týdne.
        </p>
        {activities.length === 0 ? (
          <p className="card__text">Zatím žádné odeslané aktivity.</p>
        ) : (
          <div className="jr-table-wrap">
            <table className="jr-table">
              <thead>
                <tr><th>Datum</th><th>Aktivita</th><th>Kontakt / nemovitost</th><th>Zdroj</th><th>Stav</th><th>Poznámka / výsledek</th></tr>
              </thead>
              <tbody>
                {activities.map((activity) => (
                  <tr key={activity.id}>
                    <td>{activity.date_performed.slice(0, 10)}</td>
                    <td>{ACTIVITY_TYPES[activity.activity_type] || activity.activity_type}</td>
                    <td>{activity.contact_name || '—'}</td>
                    <td>{policy.catalogues.sources.find((s) => s.code === activity.source_sphere)?.name || SOURCES[activity.source_sphere] || activity.source_sphere || '—'}</td>
                    <td>{ACTIVITY_STATUSES[activity.status] || activity.status}</td>
                    <td>{[activity.notes, activity.outcome].filter(Boolean).join(' · ') || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </article>

      {!(plan && plan.week_closed) && (
        <form className="card" onSubmit={handleAdjust}>
          <h2 className="card__title">Týdenní plán</h2>
          <p className="card__hint">
            Změna plánu se zapisuje s důvodem. Plán pod {minimumContacts} oslovení vyžaduje souhlas JCH.
            {' '}Zadávají se cíle před krácením za nepracovní dny; kalendář se odečte automaticky.
            {' '}Uzavření týdne vytvoří následující týden s hodnotami z tohoto formuláře.
          </p>
          <div className="auth__row">
            <label className="auth__label">
              Oslovení
              <input className="auth__input" type="number" min="0" required value={form.targetContacts} onChange={updateForm('targetContacts')} />
            </label>
            <label className="auth__label">
              Schůzky
              <input className="auth__input" type="number" min="0" step="0.01" value={form.targetMeetings} onChange={updateForm('targetMeetings')} />
            </label>
            <label className="auth__label">
              Nábory
              <input className="auth__input" type="number" min="0" step="0.01" value={form.targetAcquisitions} onChange={updateForm('targetAcquisitions')} />
            </label>
          </div>
          <label className="auth__label">
            Důvod změny
            <select className="auth__input" required value={form.reasonCode} onChange={updateForm('reasonCode')}>
              <option value="">— vybrat důvod —</option>
              {reasonCodes.map((r) => (
                <option key={r.code} value={r.code}>{r.name}</option>
              ))}
            </select>
          </label>
          <label className="auth__label">
            Poznámka (nepovinná)
            <input className="auth__input" type="text" value={form.reason} onChange={updateForm('reason')} />
          </label>
          {Number(form.targetContacts) < minimumContacts && form.targetContacts !== '' && (
            <label className="auth__label">
              <input
                type="checkbox"
                checked={form.jchConsent}
                onChange={(e) => setForm((p) => ({ ...p, jchConsent: e.target.checked }))}
              />{' '}
              JCH s plánem pod {minimumContacts} souhlasí
            </label>
          )}
          <div className="auth__row">
            <button className="button button--spot" type="submit" disabled={saving}>
              {saving ? 'Ukládám…' : 'Uložit plán'}
            </button>
            {plan && !plan.week_closed && (
              <button className="button button--secondary" type="submit" value="next-week" disabled={saving}>
                Uzavřít týden a zahájit další
              </button>
            )}
          </div>
        </form>
      )}

      <form className="card" onSubmit={handleVacation}>
        <h2 className="card__title">Dovolená a nepřítomnost</h2>
        <p className="card__hint">Zapisují se jen pracovní dny. Plán se za každý den zkrátí.</p>
        <div className="auth__row">
          <label className="auth__label">
            Od
            <input className="auth__input" type="date" required value={vac.from} onChange={(e) => setVac((v) => ({ ...v, from: e.target.value }))} />
          </label>
          <label className="auth__label">
            Do
            <input className="auth__input" type="date" value={vac.to} onChange={(e) => setVac((v) => ({ ...v, to: e.target.value }))} />
          </label>
        </div>
        <button className="button button--secondary" type="submit">Zapsat dovolenou</button>
        {absences.length > 0 && (
          <p className="card__hint">
            {absences.map((a) => (
              <button key={a.date} type="button" className="button button--quiet" onClick={() => handleRemoveAbsence(a.date)} title="Smazat">
                {fmtDay(a.date)} ✕
              </button>
            ))}
          </p>
        )}
      </form>

      <article className="card">
        <h2 className="card__title">Čas s JR a režim podpory</h2>
        <label className="auth__label">
          <input
            type="checkbox"
            checked={support.on}
            onChange={(e) => handleSupport({ ...support, on: e.target.checked })}
          />{' '}
          Makléř je v režimu podpory (častější kontakt s JR)
        </label>
        <label className="auth__label">
          Poznámka k podpoře
          <input
            className="auth__input"
            type="text"
            value={support.note}
            onChange={(e) => setSupport((s) => ({ ...s, note: e.target.value }))}
            onBlur={() => handleSupport(support)}
          />
        </label>
      </article>

      <MaklerProfileEditor maklerId={id} />

      {plan && plan.week_closed && (
        <div className="card">
          <h2 className="card__title">Týden uzavřen</h2>
          <p className="card__text">Tento týden je uzavřen a již nelze upravovat, dokud není vytvořen nový plán.</p>
        </div>
      )}
    </section>
  );
}
